import { useCallback, useEffect, useSyncExternalStore } from 'react';

import { useIncomingMessageSound } from '@app/pages/Leads/model';

import {
  DEFAULT_LEADS_FUNNEL_MAX_VISIBLE_COLUMNS,
  DEFAULT_LEADS_LOAD_BATCH_SIZE,
  LEADS_FUNNEL_MAX_VISIBLE_COLUMN_OPTIONS,
  LEADS_REGISTRY_LOAD_BATCH_SIZE_OPTIONS,
} from '../model/constants';
import {
  normalizeLeadsFunnelMaxVisibleColumns,
  normalizeLeadsLoadBatchSize,
  readLocalStorageItem,
  writeLocalStorageItem,
} from '../utils';

import type {
  LeadsRegistryFilterScope,
  LeadsRegistryLoadBatchSize,
} from '../model/types';

interface LeadsRegistrySettingsState {
  loadBatchSize: Record<LeadsRegistryFilterScope, LeadsRegistryLoadBatchSize>;
  maxVisibleColumns: Record<LeadsRegistryFilterScope, number>;
}

const LEADS_REGISTRY_LOAD_BATCH_SIZE_STORAGE_KEYS: Record<
  LeadsRegistryFilterScope,
  string
> = {
  registry: 'leads-v2:registry:load-batch-size',
  funnels: 'leads-v2:funnel:load-batch-size',
};

const LEADS_REGISTRY_MAX_VISIBLE_COLUMNS_STORAGE_KEYS: Record<
  LeadsRegistryFilterScope,
  string
> = {
  registry: 'leads-v2:registry:max-visible-columns',
  funnels: 'leads-v2:funnel:max-visible-columns',
};

const readStoredLoadBatchSize = (
  scope: LeadsRegistryFilterScope
): LeadsRegistryLoadBatchSize => {
  const storageKey = LEADS_REGISTRY_LOAD_BATCH_SIZE_STORAGE_KEYS[scope];
  const savedRawValue = readLocalStorageItem(storageKey);
  const savedValue = Number(savedRawValue);
  const normalizedValue = normalizeLeadsLoadBatchSize(
    savedRawValue === null ? DEFAULT_LEADS_LOAD_BATCH_SIZE : savedValue
  );

  if (savedRawValue !== null && savedValue !== normalizedValue) {
    writeLocalStorageItem(storageKey, String(normalizedValue));
  }

  return normalizedValue;
};

const readStoredMaxVisibleColumns = (
  scope: LeadsRegistryFilterScope
): number => {
  const storageKey = LEADS_REGISTRY_MAX_VISIBLE_COLUMNS_STORAGE_KEYS[scope];
  const savedRawValue = readLocalStorageItem(storageKey);
  const normalizedValue = normalizeLeadsFunnelMaxVisibleColumns(
    savedRawValue === null
      ? DEFAULT_LEADS_FUNNEL_MAX_VISIBLE_COLUMNS
      : Number(savedRawValue)
  );

  if (savedRawValue !== null && String(normalizedValue) !== savedRawValue) {
    writeLocalStorageItem(storageKey, String(normalizedValue));
  }

  return normalizedValue;
};

const readStoredSettings = (): LeadsRegistrySettingsState => ({
  loadBatchSize: {
    registry: readStoredLoadBatchSize('registry'),
    funnels: readStoredLoadBatchSize('funnels'),
  },
  maxVisibleColumns: {
    registry: readStoredMaxVisibleColumns('registry'),
    funnels: readStoredMaxVisibleColumns('funnels'),
  },
});

const saveLoadBatchSize = (
  scope: LeadsRegistryFilterScope,
  value: LeadsRegistryLoadBatchSize
) => {
  const normalizedValue = normalizeLeadsLoadBatchSize(value);

  writeLocalStorageItem(
    LEADS_REGISTRY_LOAD_BATCH_SIZE_STORAGE_KEYS[scope],
    String(normalizedValue)
  );
};

const saveMaxVisibleColumns = (
  scope: LeadsRegistryFilterScope,
  value: number
) => {
  const normalizedValue = normalizeLeadsFunnelMaxVisibleColumns(value);

  writeLocalStorageItem(
    LEADS_REGISTRY_MAX_VISIBLE_COLUMNS_STORAGE_KEYS[scope],
    String(normalizedValue)
  );
};

let leadsRegistrySettingsState = readStoredSettings();

const listeners = new Set<() => void>();

const getSnapshot = () => leadsRegistrySettingsState;

const subscribe = (listener: () => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

const notifyListeners = () => {
  listeners.forEach((listener) => {
    listener();
  });
};

const setLeadsRegistrySettingsState = (
  nextState: LeadsRegistrySettingsState
) => {
  leadsRegistrySettingsState = nextState;
  notifyListeners();
};

export const useLeadsRegistrySettings = (
  scope: LeadsRegistryFilterScope = 'registry'
) => {
  const settings = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const {
    incomingMessageSoundEnabledChangeHandler,
    incomingMessageSoundToggleHandler,
    isIncomingMessageSoundEnabled,
  } = useIncomingMessageSound();

  const loadBatchSizeChangeHandler = useCallback(
    (loadBatchSize: LeadsRegistryLoadBatchSize) => {
      const normalizedLoadBatchSize =
        normalizeLeadsLoadBatchSize(loadBatchSize);

      setLeadsRegistrySettingsState({
        ...leadsRegistrySettingsState,
        loadBatchSize: {
          ...leadsRegistrySettingsState.loadBatchSize,
          [scope]: normalizedLoadBatchSize,
        },
      });
      saveLoadBatchSize(scope, normalizedLoadBatchSize);
    },
    [scope]
  );

  const maxVisibleColumnsChangeHandler = useCallback(
    (maxVisibleColumns: number) => {
      const normalizedMaxVisibleColumns =
        normalizeLeadsFunnelMaxVisibleColumns(maxVisibleColumns);

      setLeadsRegistrySettingsState({
        ...leadsRegistrySettingsState,
        maxVisibleColumns: {
          ...leadsRegistrySettingsState.maxVisibleColumns,
          [scope]: normalizedMaxVisibleColumns,
        },
      });
      saveMaxVisibleColumns(scope, normalizedMaxVisibleColumns);
    },
    [scope]
  );

  useEffect(() => {
    setLeadsRegistrySettingsState(readStoredSettings());
  }, []);

  return {
    loadBatchSize: settings.loadBatchSize[scope],
    maxVisibleColumns: settings.maxVisibleColumns[scope],
    isIncomingMessageSoundEnabled,
    loadBatchSizeOptions: LEADS_REGISTRY_LOAD_BATCH_SIZE_OPTIONS,
    maxVisibleColumnsOptions: LEADS_FUNNEL_MAX_VISIBLE_COLUMN_OPTIONS,
    loadBatchSizeChangeHandler,
    maxVisibleColumnsChangeHandler,
    incomingMessageSoundEnabledChangeHandler,
    incomingMessageSoundToggleHandler,
  };
};
